'use client';

import { useCallback, useMemo, useState, useEffect } from 'react';
import { useLocalizationStore } from '@/core/stores/localization.store';
import { EXCHANGE_RATES, CURRENCY_META } from '@/core/constants/currencies';
import type { SupportedCurrency, DateFormatType } from '@/core/types/localization.types';

/* ------------------------------------------------------------------ */
/*  Date format mapping → Intl.DateTimeFormat options                  */
/* ------------------------------------------------------------------ */

const DATE_FORMAT_MAP: Record<DateFormatType, Intl.DateTimeFormatOptions> = {
  'DD MMM YYYY': { day: '2-digit', month: 'short', year: 'numeric' },
  'MM/DD/YYYY': { month: '2-digit', day: '2-digit', year: 'numeric' },
  'YYYY-MM-DD': { year: 'numeric', month: '2-digit', day: '2-digit' },
};

/* ------------------------------------------------------------------ */
/*  Hook                                                               */
/* ------------------------------------------------------------------ */

export function useLocalization() {
  const currency = useLocalizationStore((s) => s.currency);
  const timezone = useLocalizationStore((s) => s.timezone);
  const dateFormat = useLocalizationStore((s) => s.dateFormat);
  const timeFormat = useLocalizationStore((s) => s.timeFormat);
  const numberFormat = useLocalizationStore((s) => s.numberFormat);

  const meta = useMemo(() => CURRENCY_META[currency], [currency]);
  const rate = useMemo(() => EXCHANGE_RATES[currency], [currency]);

  // Track mount state to avoid hydration mismatch from Intl.NumberFormat
  const [mounted, setMounted] = useState(false);
  // eslint-disable-next-line react-hooks/set-state-in-effect -- hydration guard: mounted flag has no external-store equivalent
  useEffect(() => { setMounted(true); }, []);

  /**
   * Convert amount from USD (DB currency) → user's display currency and format.
   * Uses simple fallback during SSR to prevent hydration mismatch.
   */
  const formatMoney = useCallback(
    (amountInUSD: number | null | undefined): string => {
      // An absent amount is NOT zero.
      //
      // `null * rate` is 0 and `undefined * rate` is NaN, so a property with
      // no rates rendered as "$0" — read as free, not as "not yet priced".
      // 56 of 137 live properties have no room types and therefore no price
      // (D-26/D-27). "Price on request" is the only rendering a reader cannot
      // mistake for a number.
      if (typeof amountInUSD !== 'number' || !Number.isFinite(amountInUSD) || amountInUSD <= 0) {
        return 'Price on request';
      }
      const converted = amountInUSD * rate;
      if (!mounted) return `${meta.symbol}${Math.round(converted)}`;
      return new Intl.NumberFormat(meta.locale, {
        style: 'currency',
        currency: meta.code,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(converted);
    },
    [rate, meta, mounted]
  );

  /**
   * Format an ACCOUNTING amount (revenue, expense, variance, balance).
   *
   * Unlike formatMoney — a PRICE, where zero or missing means "not priced" —
   * an amount of 0 is a real figure and a negative one (a variance, a loss) is
   * shown with its sign. Only a missing value is "—".
   */
  const formatAmount = useCallback(
    (amountInUSD: number | null | undefined): string => {
      if (typeof amountInUSD !== 'number' || !Number.isFinite(amountInUSD)) return '—';
      const converted = amountInUSD * rate;
      if (!mounted) return `${converted < 0 ? '-' : ''}${meta.symbol}${Math.abs(Math.round(converted))}`;
      return new Intl.NumberFormat(meta.locale, {
        style: 'currency',
        currency: meta.code,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(converted);
    },
    [rate, meta, mounted]
  );

  /**
   * Get the currency symbol only.
   */
  const currencySymbol = useMemo(() => meta.symbol, [meta]);

  /**
   * Convert a raw number from USD → display currency (no formatting).
   */
  const convertFromUSD = useCallback(
    (amountInUSD: number): number => amountInUSD * rate,
    [rate]
  );

  /**
   * Convert from a specific source currency to the user's display currency.
   * Converts source → USD → display.
   */
  const convertCurrency = useCallback(
    (amount: number, fromCurrency: SupportedCurrency): string => {
      const inUSD = amount / EXCHANGE_RATES[fromCurrency];
      const converted = inUSD * rate;
      return new Intl.NumberFormat(meta.locale, {
        style: 'currency',
        currency: meta.code,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(converted);
    },
    [rate, meta]
  );

  /**
   * Format a UTC date string/Date object → user's timezone + date format.
   */
  const formatLocalDate = useCallback(
    (date: string | Date, options?: Intl.DateTimeFormatOptions): string => {
      const d = typeof date === 'string' ? new Date(date) : date;
      const baseOpts = DATE_FORMAT_MAP[dateFormat] || DATE_FORMAT_MAP['DD MMM YYYY'];
      return new Intl.DateTimeFormat('en', {
        ...baseOpts,
        ...options,
        timeZone: timezone,
      }).format(d);
    },
    [dateFormat, timezone]
  );

  /**
   * Format a UTC date string/Date object → user's timezone + date + time.
   */
  const formatLocalDateTime = useCallback(
    (date: string | Date): string => {
      const d = typeof date === 'string' ? new Date(date) : date;
      const baseOpts = DATE_FORMAT_MAP[dateFormat] || DATE_FORMAT_MAP['DD MMM YYYY'];
      const hourOpts: Intl.DateTimeFormatOptions = timeFormat === '24'
        ? { hour: '2-digit', minute: '2-digit', hour12: false }
        : { hour: '2-digit', minute: '2-digit', hour12: true };
      return new Intl.DateTimeFormat('en', {
        ...baseOpts,
        ...hourOpts,
        timeZone: timezone,
      }).format(d);
    },
    [dateFormat, timeFormat, timezone]
  );

  /**
   * Format a UTC date string/Date object → time only in user's timezone.
   */
  const formatLocalTime = useCallback(
    (date: string | Date): string => {
      const d = typeof date === 'string' ? new Date(date) : date;
      const hourOpts: Intl.DateTimeFormatOptions = timeFormat === '24'
        ? { hour: '2-digit', minute: '2-digit', hour12: false }
        : { hour: '2-digit', minute: '2-digit', hour12: true };
      return new Intl.DateTimeFormat('en', {
        ...hourOpts,
        timeZone: timezone,
      }).format(d);
    },
    [timeFormat, timezone]
  );

  /**
   * Format a number using the user's number format preference.
   */
  const formatNumber = useCallback(
    (num: number): string => {
      const locale = numberFormat === 'indian' ? 'en-IN' : 'en-US';
      return new Intl.NumberFormat(locale).format(num);
    },
    [numberFormat]
  );

  return {
    // Current settings
    currency,
    timezone,
    dateFormat,
    timeFormat,
    numberFormat,
    currencySymbol,
    // Formatters
    formatMoney,
    formatAmount,
    convertFromUSD,
    convertCurrency,
    formatLocalDate,
    formatLocalDateTime,
    formatLocalTime,
    formatNumber,
  };
}
